import React from 'react';
import type { ChatMessage } from './ChatBot';
import {
  useWidgetAnnouncer,
  type AnnounceOptions,
} from './WidgetAnnouncerProvider';
import { stripMarkdownForAnnouncement } from './stripMarkdownForAnnouncement';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { getActiveElementIn, getIntakeIntro } from './dialogFocus';

interface UseChatDrawerA11yOptions {
  open: boolean;
  agentName: string;
  history: ChatMessage[];
  chatState: 'idle' | 'pending';
  isServiceDown: boolean;
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** The intake form is pending: focus its intro sentence on open instead of the message input. */
  intakePending?: boolean;
  dialogRef?: React.RefObject<HTMLElement | null>;
}

function announceLatestUnannouncedBotReply(
  history: ChatMessage[],
  announcedTimestamps: Set<number>,
  announce: (message: string, options?: AnnounceOptions) => void,
) {
  const lastBot = [...history].reverse().find((msg) => msg.role === 'bot' && msg.text);
  if (lastBot && !announcedTimestamps.has(lastBot.timestamp)) {
    announcedTimestamps.add(lastBot.timestamp);
    // Bot reply text is not catalog-translated — omit lang.
    announce(stripMarkdownForAnnouncement(lastBot.text));
  }
}

/** Lets the visible privacy status announce before auto-focus moves to the input. */
const INPUT_FOCUS_DELAY_MS = 300;

const FOCUSABLE_SELECTOR =
  'button, a[href], input, textarea, select, [tabindex]:not([tabindex="-1"])';

/** Avoid stealing focus when the user tabs to skip link, header controls, or the host page. */
function canAutoFocus(input: HTMLElement): boolean {
  const active = getActiveElementIn(input);
  if (active === input) return false;
  if (!active || active === document.body) return true;
  if (active instanceof HTMLElement && active.matches(FOCUSABLE_SELECTOR)) {
    return false;
  }
  return true;
}

export function useChatDrawerA11y({
  open,
  agentName,
  history,
  chatState,
  isServiceDown,
  inputRef,
  intakePending = false,
  dialogRef,
}: UseChatDrawerA11yOptions) {
  const { announce } = useWidgetAnnouncer();
  const { t, locale } = useWidgetI18n();
  const prevPendingRef = React.useRef(false);
  const prevChatStateRef = React.useRef(chatState);
  const announcedBotTimestampsRef = React.useRef<Set<number>>(new Set());
  const announcedOpenRef = React.useRef(false);

  // Keyed on `open` alone and guarded by a ref. Listing `history` here made
  // every unrelated re-render tear this effect down, and because the drawer
  // was already open the sequence never ran again.
  React.useEffect(() => {
    if (!open) {
      announcedOpenRef.current = false;
      return;
    }
    if (announcedOpenRef.current) return;
    announcedOpenRef.current = true;

    announce(t('announce.chatOpened', { agentName }), { lang: locale });

    // The outage screen replaces the message log and the privacy notice
    // entirely, so nothing that belongs to the transcript may be announced.
    // The last bot bubble is usually the failure copy, which would otherwise
    // repeat what the focused outage heading already says.
    if (!isServiceDown) {
      announceLatestUnannouncedBotReply(history, announcedBotTimestampsRef.current, announce);
    }

    // Defer focus so the persistent PrivacyNotice status can announce first.
    const focusDelayMs = isServiceDown ? 0 : INPUT_FOCUS_DELAY_MS;
    const focusTimer = window.setTimeout(() => {
      const dialog = dialogRef?.current;
      const intro = intakePending && dialog ? getIntakeIntro(dialog) : null;
      const target = intro ?? inputRef.current;
      if (target && canAutoFocus(target)) {
        target.focus();
      }
    }, focusDelayMs);
    return () => window.clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The outage is deliberately not announced from here. VoiceOver ignores
  // `aria-live` inside a shadow root, so ServiceUnavailableContent takes focus
  // on its heading instead — announcing it in both places would make the
  // screen readers that *do* support shadow live regions say it twice.

  React.useEffect(() => {
    if (!open) {
      prevPendingRef.current = false;
      prevChatStateRef.current = chatState;
    }
  }, [open, chatState]);

  React.useEffect(() => {
    if (!open) return;

    const hasPendingBot = history.some((msg) => msg.role === 'bot' && !msg.text);
    const wasPending = prevPendingRef.current;
    // Tracked even while down so recovery does not replay a stale transition.
    prevPendingRef.current = hasPendingBot;
    if (isServiceDown) return;

    if (hasPendingBot && !wasPending) {
      announce(t('announce.assistantReplying'), { lang: locale });
    }
  }, [history, announce, open, isServiceDown, t, locale]);

  React.useEffect(() => {
    if (!open) return;

    const wasPending = prevChatStateRef.current === 'pending';
    prevChatStateRef.current = chatState;
    if (isServiceDown) return;

    if (wasPending && chatState === 'idle') {
      announceLatestUnannouncedBotReply(history, announcedBotTimestampsRef.current, announce);
    }
  }, [chatState, history, announce, open, isServiceDown]);
}
